/**
 * auth-return-routing — CANONICAL decision for what the app must do when an
 * auth email return lands OUTSIDE `/auth/callback`.
 *
 * PROVEN DEFECT THIS MODULE FIXES
 * -------------------------------
 * A verification email whose `redirect_to` was dropped by GoTrue falls back to
 * the Site URL: the user verifies successfully, GoTrue redirects to `/`, the
 * session is established — and the app silently rendered the marketing
 * homepage, stranding the customer mid-funnel with their pending scan
 * invisible.
 *
 * Rule (system-wide, fail closed):
 *   If a FRESH sign-in completes anywhere outside the auth routes AND this
 *   browser holds a pending Free Scan identity for THE SAME email, the app
 *   routes into `/auth/callback?scan_request_id=S` so the canonical callback
 *   resumes the scan. Identity is never inferred from "latest", "first",
 *   brand fallback, or ambient workspace state — only from the browser-local
 *   pending-scan handle, and only when the signed-in email matches it.
 *
 * Non-matches render normally (homepage stays the homepage):
 *   • no pending scan in this browser (e.g. clicked on another device)
 *   • signed-in email differs from the pending scan's email (wrong account —
 *     account-switch UI owns that case, we never hijack)
 *   • already on an auth route
 *
 * PURE module: no React, no Supabase, no side effects.
 */

export const AUTH_CALLBACK_PATH = "/auth/callback";
export const AUTH_RECOVERY_PATH = "/auth/link-expired";

/** Routes that must never be hijacked by the pending-scan resume rule. */
const EXCLUDED_PATHS = new Set([
  AUTH_CALLBACK_PATH,
  AUTH_RECOVERY_PATH,
  "/auth/reset-password",
]);

export interface PendingScanHandle {
  scan_request_id: string;
  email?: string | null;
}

export type AuthReturnDecision =
  | { type: "resume_scan"; to: string }
  | { type: "none" };

/**
 * Decide whether a fresh sign-in must resume a pending Free Scan.
 *
 * @param pathname      current location pathname
 * @param pendingScan   browser-local pending scan handle (source B), or null
 * @param signedInEmail email of the JUST-signed-in user (from the SIGNED_IN
 *                      event), or null when unknown
 */
export function resolveAuthReturnRoute(args: {
  pathname: string;
  pendingScan: PendingScanHandle | null;
  signedInEmail?: string | null;
}): AuthReturnDecision {
  const pathname = args.pathname;
  if (EXCLUDED_PATHS.has(pathname)) return { type: "none" };

  const scanRequestId = args.pendingScan?.scan_request_id?.trim();
  if (!scanRequestId) return { type: "none" };

  // Fail closed on identity mismatch: the pending scan belongs to a specific
  // email. A sign-in as anyone else must never resume it.
  const pendingEmail = args.pendingScan?.email?.trim().toLowerCase();
  const signedInEmail = args.signedInEmail?.trim().toLowerCase();
  if (!signedInEmail) return { type: "none" };
  if (pendingEmail && signedInEmail !== pendingEmail) return { type: "none" };

  return {
    type: "resume_scan",
    to: `${AUTH_CALLBACK_PATH}?scan_request_id=${encodeURIComponent(scanRequestId)}`,
  };
}
